import { showSuccess, showError } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import type { EndpointBuilder } from '@reduxjs/toolkit/query'

import {
  INVITEE_TAG_TYPE,
  MEETING_BUFFER_TAG_TYPE,
  MEETINGS_REALTIME_TAG_TYPE,
  buildMeetingListTag,
  buildMeetingTag,
  buildMeetingBufferTag
} from './constants'
import {
  applyInviteeEmailToCache,
  applyNoShowResponseToCache,
  buffersOptimisticUpdate,
  meetingNotesOptimisticUpdate,
  noShowOptimisticUpdate,
  blockOptimisticUpdate
} from './optimistic_updates'
import type {
  BlockInviteeArgs,
  CancelInviteeArgs,
  CancelMeetingArgs,
  MarkInviteeAsNoShowArgs,
  UnblockInviteeArgs,
  UpdateBufferArgs,
  UpdateInviteeArgs,
  UpdateMeetingArgs
} from './types'
import { getBufferKey, getUpdateMeetingErrorMessage } from './utils'

/**
 * Handles optimistic update rollback on mutation failure.
 * Awaits the query fulfillment and undoes all patches if the mutation fails.
 */
const handleOptimisticUpdate = async (
  queryFulfilled: Promise<unknown>,
  patches: Array<{ undo: () => void }>
) => {
  try {
    await queryFulfilled
  } catch {
    // If the mutation fails, undo the optimistic updates
    patches.forEach((patch) => patch.undo())
  }
}

/** API slice shape required by optimistic update helpers (updateQueryData, selectInvalidatedBy, etc.). */
type MeetingsApiForOptimisticUpdates = Parameters<typeof applyNoShowResponseToCache>[0]['api']

export const buildMutations = (
  build: EndpointBuilder<any, any, any>,
  meetingsApi?: MeetingsApiForOptimisticUpdates
) => {
  const api = meetingsApi
  return {
  /**
   * @deprecated Use `updateMeeting` instead. This mutation invalidates all Meeting tags,
   * while `updateMeeting` provides more targeted cache invalidation by only invalidating
   * the specific meeting being updated, resulting in better performance.
   */
    updateEventAttributes: build.mutation({
      query: ({ id, attributes }: UpdateMeetingArgs) => ({
        url: `/api/dashboard/events/${id}`,
        method: 'PUT',
        data: attributes
      }),
      onQueryStarted: async ({ id, attributes }: UpdateMeetingArgs, { dispatch, getState, queryFulfilled }: any) => {
      // Only perform optimistic update if internal_notes is being updated
        if (!attributes || !('internal_notes' in attributes)) {
          return
        }

        const patches = meetingNotesOptimisticUpdate({
          api,
          dispatch,
          state: getState(),
          eventId: id,
          internalNotes: attributes.internal_notes || ''
        })

        await handleOptimisticUpdate(queryFulfilled, patches)
      },
      invalidatesTags: (_result: unknown, error: unknown, args: UpdateMeetingArgs) => (error ? [] : [
        buildMeetingListTag(),
        buildMeetingTag(args.id),
        MEETINGS_REALTIME_TAG_TYPE
      ])
    }),
    updateInvitee: build.mutation({
      query: ({ id, attributes }: UpdateInviteeArgs) => ({
        url: `/api/dashboard/invitees/${id}`,
        method: 'PUT',
        data: attributes
      }),
      onQueryStarted: async (
        { id, eventId, eventUuid, attributes }: UpdateInviteeArgs,
        { dispatch, getState, queryFulfilled }: any
      ) => {
        try {
          await queryFulfilled
          const email = attributes?.email
          if (email == null || email === '') return
          // Patch GraphQL Supergraph caches: REST invalidatesTags cannot touch root-api-graphql.
          applyInviteeEmailToCache({
            api,
            dispatch,
            state: getState(),
            eventId,
            eventUuid,
            inviteeId: id,
            email,
          })
        } catch {
          // Mutation failed; error handling remains in the modal / base query.
        }
      },
      invalidatesTags: (result: { eventId?: string | number } | undefined, error: unknown, args: UpdateInviteeArgs) => {
        if (error) return []
        const tags: Array<{ type: string; id?: string } | string> = [
          buildMeetingListTag(),
          INVITEE_TAG_TYPE,
          MEETINGS_REALTIME_TAG_TYPE
        ]
        // Prefer arg ids: the dashboard invitee presenter does not return eventId.
        // Supergraph scheduled-event detail is tagged with Meeting:{uuid}, so eventUuid is required
        // for the right-rail solo invitee.email to refresh without a full page reload.
        if (args?.eventId != null) {
          tags.push(buildMeetingTag(args.eventId))
        } else if (result?.eventId != null) {
          tags.push(buildMeetingTag(result.eventId))
        }
        if (args?.eventUuid != null && String(args.eventUuid) !== String(args.eventId)) {
          tags.push(buildMeetingTag(args.eventUuid))
        }
        return tags
      },
      extraOptions: {
        onSuccess: ({ name }: { name: string }, { dispatch }: { dispatch: (action: unknown) => void }) => {
          dispatch(
            showSuccess(
              I18n.t('publisher.meetings.edit_email_modal.success_toast', { name })
            )
          )
        }
      }
    }),
    cancelInvitee: build.mutation({
      query: ({ inviteeId, cancelReason }: CancelInviteeArgs) => ({
        url: `/api/dashboard/invitees/${inviteeId}/cancel`,
        method: 'PUT',
        data: { cancelReason }
      }),
      invalidatesTags: (_result: unknown, error: unknown, args: CancelInviteeArgs) => {
        if (error) return []
        const tags: Array<{ type: string; id?: string } | string> = [
          buildMeetingListTag(),
          INVITEE_TAG_TYPE,
          MEETINGS_REALTIME_TAG_TYPE
        ]
        // Only add individual meeting tag if eventId is provided
        if (args.eventId) {
          tags.push(buildMeetingTag(args.eventId))
        }
        return tags
      }
    }),
    markInviteeAsNoShow: build.mutation({
      query: ({ id }: MarkInviteeAsNoShowArgs) => ({
        url: '/api/dashboard/invitee_dispositions/no_shows',
        method: 'POST',
        data: {
          inviteeId: id
        }
      }),
      onQueryStarted: async (
        { id, eventId, eventUuid }: MarkInviteeAsNoShowArgs,
        { dispatch, getState, queryFulfilled }: any
      ) => {
        const patches = noShowOptimisticUpdate({
          api, dispatch, state: getState(), eventId, eventUuid, inviteeId: id, value: true
        })

        try {
          const { data } = await queryFulfilled
          if (data) {
            applyNoShowResponseToCache({
              api,
              state: getState(),
              dispatch,
              eventId,
              eventUuid,
              responseInvitee: data
            })
          }
        } catch {
          patches.forEach((patch) => patch.undo())
        }
      },
      invalidatesTags: (_result: unknown, error: unknown, args: MarkInviteeAsNoShowArgs) => {
        if (error) return []
        const tags: Array<{ type: string; id?: string } | string> = [
          buildMeetingListTag(),
          buildMeetingTag(args.eventId),
          INVITEE_TAG_TYPE,
          MEETINGS_REALTIME_TAG_TYPE
        ]
        if (args.eventUuid) {
          tags.push(buildMeetingTag(args.eventUuid))
        }
        return tags
      }
    }),
    undoNoShowForInvitee: build.mutation({
      query: ({ id }: MarkInviteeAsNoShowArgs) => ({
        url: '/api/dashboard/invitee_dispositions/no_shows',
        method: 'DELETE',
        data: {
          inviteeId: id
        }
      }),
      onQueryStarted: async (
        { id, eventId, eventUuid }: MarkInviteeAsNoShowArgs,
        { dispatch, getState, queryFulfilled }: any
      ) => {
        const patches = noShowOptimisticUpdate({
          api, dispatch, state: getState(), eventId, eventUuid, inviteeId: id, value: false
        })

        try {
          const { data } = await queryFulfilled
          const responseInvitee = data ?? { id, noShow: false }
          applyNoShowResponseToCache({
            api,
            state: getState(),
            dispatch,
            eventId,
            eventUuid,
            responseInvitee
          })
        } catch {
          patches.forEach((patch) => patch.undo())
        }
      },
      invalidatesTags: (_result: unknown, error: unknown, args: MarkInviteeAsNoShowArgs) => {
        if (error) return []
        const tags: Array<{ type: string; id?: string } | string> = [
          buildMeetingListTag(),
          buildMeetingTag(args.eventId),
          INVITEE_TAG_TYPE,
          MEETINGS_REALTIME_TAG_TYPE
        ]
        if (args.eventUuid) {
          tags.push(buildMeetingTag(args.eventUuid))
        }
        return tags
      }
    }),
    updateMeeting: build.mutation({
      query: ({ id, attributes }: UpdateMeetingArgs) => ({
        url: `/api/dashboard/events/${id}`,
        method: 'PUT',
        data: attributes
      }),
      extraOptions: {
        handleErrors: false,
      },
      onQueryStarted: async ({ id, attributes }: UpdateMeetingArgs, { dispatch, getState, queryFulfilled }: any) => {
        let patches: Array<{ undo: () => void }> = []

        // Only perform an optimistic update when internal_notes is being updated.
        if (attributes && 'internal_notes' in attributes) {
          patches = meetingNotesOptimisticUpdate({
            api,
            dispatch,
            state: getState(),
            eventId: id,
            internalNotes: attributes.internal_notes || ''
          })
        }

        try {
          await queryFulfilled
        } catch (rejection: any) {
          patches.forEach((patch) => patch.undo())
          const message = getUpdateMeetingErrorMessage(rejection?.error)
          if (message) {
            dispatch(showError(message))
          }
        }
      },
      invalidatesTags: (_result: unknown, error: unknown, arg: UpdateMeetingArgs) => (error ? [] : [
        buildMeetingListTag(),
        buildMeetingTag(arg.id),
        MEETINGS_REALTIME_TAG_TYPE,
      ]),
    }),
    cancelMeeting: build.mutation({
      query: ({ eventId, reason, reasonType, additionalInfo, shareMethods }: CancelMeetingArgs) => ({
        url: `/api/dashboard/events/${eventId}/cancel`,
        method: 'PUT',
        data: {
          cancel_reason: reason,
          reason_type: reasonType,
          additional_information: additionalInfo,
          share_methods: shareMethods
        },
      }),
      invalidatesTags: (_result: unknown, error: unknown, args: CancelMeetingArgs) => (error ? [] : [
        buildMeetingListTag(),
        buildMeetingTag(args.eventId),
        MEETINGS_REALTIME_TAG_TYPE
      ])
    }),
    updateBuffer: build.mutation({
      query: ({ eventId, membershipId, isBefore, duration }: UpdateBufferArgs) => ({
        url: `/api/dashboard/events/${eventId}`,
        method: 'PATCH',
        data: {
          membership_buffer_attributes: { id: membershipId, [getBufferKey(isBefore)]: duration }
        },
        duration
      }),
      onQueryStarted: async (
        { eventId, membershipId, isBefore, duration }: UpdateBufferArgs,
        { dispatch, getState, queryFulfilled }: any
      ) => {
        const patches = buffersOptimisticUpdate({
          api, dispatch, state: getState(), eventId, membershipId, isBefore, duration
        })

        await handleOptimisticUpdate(queryFulfilled, patches)
      },
      invalidatesTags: (
        _result: unknown,
        error: unknown,
        { eventId, membershipId }: UpdateBufferArgs
      ) => (error ? [] : [
        buildMeetingListTag(),
        buildMeetingTag(eventId),
        buildMeetingBufferTag(eventId),
        { type: MEETING_BUFFER_TAG_TYPE, id: `${eventId}-${membershipId}` }
      ]),
      extraOptions: {
        onSuccess: (
          _result: unknown,
          { dispatch }: { dispatch: (action: unknown) => void },
          { duration }: UpdateBufferArgs
        ) => {
          const isDeleted = Number(duration) === 0

          dispatch(
            showSuccess(
              isDeleted
                ? I18n.t('publisher.scheduled_events.buffers.edit_buffer_modal.buffer_time_deleted')
                : I18n.t('publisher.scheduled_events.buffers.edit_buffer_modal.buffer_time_updated')
            )
          )
        }
      },
    }),
    deletePendingEvent: build.mutation({
      query: (id: string | number) => ({
        method: 'DELETE',
        url: `/api/scheduled_events/pending_events/${id}`
      }),
      invalidatesTags: (_result: unknown, error: unknown) => (error ? [] : [
        buildMeetingListTag(),
        MEETINGS_REALTIME_TAG_TYPE
      ])
    }),
    bookPoll: build.mutation({
      query: ({ eventTypeId, inviteeIds, startTime, locationConfiguration, additionalInvitees }: {
      eventTypeId: string | number
      inviteeIds: string[]
      startTime: string
      locationConfiguration: Record<string, unknown>
      additionalInvitees?: Array<Record<string, unknown>>
    }) => ({
        url: `/api/scheduled_events/polls/${eventTypeId}/schedule`,
        method: 'POST',
        data: {
          event: {
            startTime,
            locationConfiguration
          },
          inviteeIds,
          additionalInvitees
        }
      }),
      invalidatesTags: (_result: unknown, error: unknown) => (error ? [] : [
        buildMeetingListTag(),
        MEETINGS_REALTIME_TAG_TYPE
      ])
    }),
    blockInvitee: build.mutation({
      query: ({
        inviteeUuid, hostId, inviteeName, inviteeEmail, meetingName, reasonType, reason
      }: BlockInviteeArgs) => ({
        url: `/api/users/${hostId}/blocked_sources_settings`,
        method: 'POST',
        data: {
          invitee_uuid: inviteeUuid,
          invitee_name: inviteeName,
          invitee_email: inviteeEmail,
          meeting_name: meetingName,
          reason_type: reasonType,
          reason
        }
      }),
      extraOptions: {
        handleErrors: false
      },
      onQueryStarted: async (
        { inviteeUuid, eventId }: BlockInviteeArgs,
        { dispatch, getState, queryFulfilled }: any
      ) => {
      // Optimistically update UI to show blocked state immediately
        const patches = blockOptimisticUpdate({
          api, dispatch, state: getState(), eventId, inviteeId: inviteeUuid, blockedByHost: true, blockUuid: null
        })

        try {
          await queryFulfilled
        } catch {
        // Undo optimistic updates on failure
          patches.forEach((patch) => patch.undo())
          dispatch(showError(I18n.t('publisher.block_invitee.error.unable_to_create_block')))
        }
      },
      invalidatesTags: (_result: unknown, error: unknown, args: BlockInviteeArgs) => {
        if (error) return []
        const tags: Array<{ type: string; id?: string } | string> = [
          buildMeetingListTag(),
          INVITEE_TAG_TYPE,
          MEETINGS_REALTIME_TAG_TYPE
        ]
        if (args?.eventId) {
          tags.push(buildMeetingTag(args.eventId))
        }
        return tags
      }
    }),
    unblockInvitee: build.mutation({
      query: ({ hostId, blockUuid, location }: UnblockInviteeArgs) => ({
        url: `/api/users/${hostId}/blocked_sources_settings/${blockUuid}`,
        method: 'DELETE',
        ...(location ? { params: { location } } : {}),
      }),
      onQueryStarted: async (
        { inviteeUuid, eventId }: UnblockInviteeArgs,
        { dispatch, getState, queryFulfilled }: any
      ) => {
        const patches = blockOptimisticUpdate({
          api, dispatch, state: getState(), eventId, inviteeId: inviteeUuid, blockedByHost: false, blockUuid: null
        })

        try {
          await queryFulfilled
        } catch {
        // Undo optimistic updates on failure
          patches.forEach((patch) => patch.undo())
          dispatch(showError(I18n.t('publisher.block_invitee.error.unable_to_unblock')))
        }
      },
      invalidatesTags: (_result: unknown, error: unknown, args: UnblockInviteeArgs) => {
        if (error) return []
        const tags: Array<{ type: string; id?: string } | string> = [
          buildMeetingListTag(),
          INVITEE_TAG_TYPE,
          MEETINGS_REALTIME_TAG_TYPE
        ]
        if (args?.eventId) {
          tags.push(buildMeetingTag(args.eventId))
        }
        return tags
      }
    })
  }
}
